import React from 'react';
import { Card, CardContent, Typography, Chip, Stack, Box, IconButton } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import CustomButton from '../Button/CustomButton';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { IActivityFeedItem } from '../../redux/activity/types';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';
import { convertMinuteSecondsDays, formatCurrency, formatNumberWithK, getJWtDetails } from '../../utils/AppUtils';
import { StarRating } from '../EsVideoVotingModal/EsRatingStars';
import { BackgroundColorCard } from './BackGroundColorCard';
import { Bookmark } from '@mui/icons-material';
import { IAudition } from '../../types/global/helper';
import { CompanyAvatar } from '../Avatar/CompanyAvatar';
import { useDispatch } from 'react-redux';
import { toggleAuthModal } from '../../redux/authentication/action';
import { RichTextViewer } from '../Common/RichTextViewer';

export interface IJobProps {
    job: IActivityFeedItem;
    onRecommendClick: () => void;
    onDetailsClick: (job?: IAudition) => void;
    onBookmarkJob: () => void;
    onDeleteBookmark: (id: string) => void;
}

function startsWithSymbol(text: string) {
    // Check if the first character is not a letter or digit
    return /^[^a-zA-Z0-9]/.test(text);
}

const JobCard: React.FC<IJobProps> = ({ job, onRecommendClick, onDetailsClick, onBookmarkJob, onDeleteBookmark }) => {
    const { themeStyle } = useThemeContext();
    const { auditions_activity } = job;
    const { isSignedIn } = getJWtDetails();
    const dispatch = useDispatch();

    const handleOnBookmarkClick = () => {
        if (!isSignedIn) {
            dispatch(toggleAuthModal(true));
            return;
        }
        if (
            auditions_activity &&
            auditions_activity.user_bookmark &&
            auditions_activity?.has_user_bookmarked &&
            auditions_activity.user_bookmark.length > 0
        ) {
            onDeleteBookmark(auditions_activity.user_bookmark[0]);
        } else {
            onBookmarkJob();
        }
    };
    return (
        <Card
            sx={{
                width: '100%',
                borderRadius: 2,
                overflow: 'visible',
                my: 2,
                backgroundColor: theme[themeStyle].secondaryBg,
            }}>
            <CardContent sx={{ px: 0 }}>
                {/* Skill Match Percentage */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" px={2}>
                    <Stack direction="row" alignItems="center" gap={'10px'}>
                        <ThspianAvatar user={auditions_activity?.created_by!} enableDefaultNavigation withMonogram />
                        <Stack>
                            <Stack direction="row" gap={'3px'}>
                                <Typography
                                    style={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].tag_white,
                                        fontSize: 10,
                                    }}>
                                    {auditions_activity?.created_by.firstName}
                                </Typography>
                                <Typography
                                    style={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].tag_white,
                                        fontSize: 10,
                                    }}>
                                    {job.activity_type}
                                </Typography>
                            </Stack>
                            <Stack alignItems="flex-start">
                                <Typography
                                    style={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].tag_white,
                                        fontSize: 8,
                                    }}>
                                    {convertMinuteSecondsDays(job.createdAt)} ago
                                </Typography>
                            </Stack>
                        </Stack>
                    </Stack>

                    {/* Bookmark Icon */}
                    <IconButton onClick={handleOnBookmarkClick}>
                        {!auditions_activity?.has_user_bookmarked ? (
                            <BookmarkBorderIcon sx={{ color: theme[themeStyle].tag_white }} />
                        ) : (
                            <Bookmark sx={{ color: theme[themeStyle].tag_white }} />
                        )}
                    </IconButton>
                </Stack>
                {
                    <Stack sx={{ maxHeight: '300px', overflow: 'hidden', width: '100%', my: 2 }} position="relative">
                        {isSignedIn && (
                            <Stack
                                position="absolute"
                                top={20}
                                right={5}
                                borderRadius={1}
                                bgcolor={'rgba(0,0,0,0.6)'}
                                justifyContent="center"
                                alignItems="center"
                                p={1}
                                zIndex={999}>
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        color: theme[themeStyle].true_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    {auditions_activity?.percentage_match}% match with your skill
                                </Typography>
                            </Stack>
                        )}
                        {auditions_activity?.imageCover && <CustomLazyLoadImage src={auditions_activity?.imageCover} />}
                        {!auditions_activity?.imageCover &&
                            auditions_activity?.is_background_color &&
                            auditions_activity.background_color && (
                                <BackgroundColorCard
                                    backgroundColor={auditions_activity.background_color}
                                    text={auditions_activity.title}
                                />
                            )}
                    </Stack>
                }

                {/* Title */}
                <Stack alignItems="flex-start" px={2}>
                    {auditions_activity?.imageCover && (
                        <Typography
                            sx={{
                                marginBottom: 1,
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                            }}>
                            {auditions_activity?.title}
                        </Typography>
                    )}

                    {/* Description */}

                    <RichTextViewer
                        content={auditions_activity?.synopsis!}
                        textStyle={{
                            padding: 0,
                            color: theme[themeStyle].tag_white,
                            marginBottom: 2,
                            textAlign: 'left',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 12,
                            display: '-webkit-box',
                            overflow: 'hidden',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 3,
                        }}
                    />
                </Stack>

                {/* Tags */}
                <Stack direction="row" spacing={1} sx={{ marginBottom: 2 }} px={2}>
                    {auditions_activity?.tags.map((tag, index) => (
                        <Chip
                            label={`${tag}`}
                            variant="outlined"
                            key={index}
                            size="small"
                            sx={{
                                backgroundColor: theme[themeStyle].darkGray60, // Light green background
                                color: theme[themeStyle].tag_white, // White text
                                borderRadius: '4px', // Reduced border radius
                                borderColor: 'transparent', // Remove border if desired
                                fontWeight: 500, // Optional: Make text bold for better readability
                                textTransform: 'capitalize',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 10,
                            }}
                        />
                    ))}
                </Stack>

                {/* Details */}
                <Stack direction="row" alignItems="center" gap={'15px'} px={2}>
                    {/* Price and Type */}
                    {!!auditions_activity?.min_reward && !!auditions_activity?.max_reward && (
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 12,
                                    color: theme[themeStyle].tag_white,
                                }}>
                                Pays up to{' '}
                                {startsWithSymbol(String(auditions_activity?.max_reward))
                                    ? auditions_activity?.max_reward
                                    : formatCurrency(auditions_activity?.max_reward, auditions_activity.currency)}
                            </Typography>
                        </Box>
                    )}
                    <Box
                        sx={{
                            backgroundColor: theme[themeStyle].darkGray60,
                            p: 0.5,
                            borderRadius: 0.5,
                        }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 12,
                                color: theme[themeStyle].tag_white,
                                textTransform: 'capitalize',
                            }}>
                            {auditions_activity?.employment_type}
                        </Typography>
                    </Box>

                    {/* Location */}
                    <Stack direction="row" alignItems="center" spacing={0.5} px={2}>
                        <LocationOnIcon sx={{ color: theme[themeStyle].tag_white }} />
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 12,
                                color: theme[themeStyle].tag_white,
                                textTransform: 'capitalize',
                            }}>
                            {auditions_activity?.shoot_location?.address}
                        </Typography>
                    </Stack>
                </Stack>

                {/* Footer */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2, pr: 1 }} px={2}>
                    {/* Avatar and Info */}
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <CompanyAvatar company={auditions_activity?.production_company!} enableDefaultNavigation />
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 10,
                                    color: theme[themeStyle].tag_white,
                                }}>
                                {auditions_activity?.production_company.company_name}
                            </Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5} px={2}>
                        <StarRating
                            disabled
                            style={{
                                paddingLeft: 0,
                                paddingRight: 0,
                                height: '10px',
                                width: 'fit-content',
                            }}
                            ratingProps={{
                                value: auditions_activity?.production_company.rating,
                                readOnly: true,
                                sx: {
                                    fontSize: 16,
                                    color: theme[themeStyle].highlight,
                                },
                            }}
                        />
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 10,
                                color: theme[themeStyle].tag_white,
                            }}>
                            {formatNumberWithK(auditions_activity?.production_company.number_of_reviews!)}
                        </Typography>
                    </Stack>
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={0.5} px={2}>
                            <WorkOutlineIcon sx={{ fontSize: '1rem', color: theme[themeStyle].highlight }} />
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 10,
                                    color: theme[themeStyle].highlight,
                                }}>
                                {auditions_activity?.production_company.number_of_jobs} Job
                                {auditions_activity &&
                                auditions_activity?.production_company &&
                                auditions_activity?.production_company.number_of_jobs &&
                                auditions_activity?.production_company.number_of_jobs > 1
                                    ? 's'
                                    : ''}
                            </Typography>
                        </Stack>
                    </Box>
                </Stack>
                <Stack
                    direction={{
                        xs: 'column',
                        md: 'row',
                    }}
                    gap={'10px'}
                    px={2}
                    my={2}>
                    <CustomButton
                        label="Details"
                        onClick={() => onDetailsClick(auditions_activity)}
                        sx={{
                            flex: 1,
                        }}
                    />
                    <CustomButton
                        label="Recommend"
                        onClick={onRecommendClick}
                        buttonProps={{
                            variant: 'text',
                        }}
                        sx={{
                            border: `1px solid ${theme[themeStyle].highlight}`,
                            flex: 1,
                        }}
                    />
                </Stack>
            </CardContent>
        </Card>
    );
};

export { JobCard };
